import { useWorkspaceFileRecords } from "@/api/fileRecords";
import type { FilesRailFileMenuActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailFileRow";
import { useFilesRailRootItems } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailTree";
import type { WorkspaceDropPayload } from "@/pages/workspaces/components/WorkspaceDropZone";
import { useFileRecordCardActions } from "@/pages/workspaces/hooks/useFileRecordCardActions";
import { useWorkspaceUploadQueue } from "@/pages/workspaces/hooks/useWorkspaceUploadQueue";
import type { FileRecord } from "@cohesive/api-client";
import { useCallback, useMemo, useRef } from "react";

export type FilesRailOsDropHandler = (payload: WorkspaceDropPayload) => void;
export type FilesRailOsDropAtPath = (
  parentPath: string,
) => FilesRailOsDropHandler;

export function useFilesRailWorkspaceFiles(
  workspaceId: string | undefined,
  options?: { enabled?: boolean },
) {
  const enabled = (options?.enabled ?? true) && !!workspaceId;

  const {
    data: fileRecords,
    isPending,
    isError,
  } = useWorkspaceFileRecords(workspaceId, { enabled });

  const uploadQueue = useWorkspaceUploadQueue(workspaceId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTargetPathRef = useRef("");

  const { dialogs, actions } = useFileRecordCardActions({
    workspaceId: workspaceId ?? "",
    allRecords: fileRecords ?? [],
    onFolderOpen: () => {},
  });

  const menuActions = useMemo((): FilesRailFileMenuActions | undefined => {
    if (!workspaceId) return undefined;
    return {
      onRename: actions.rename,
      onMove: actions.move,
      onCopy: actions.copy,
      onDelete: actions.delete,
    };
  }, [workspaceId, actions.rename, actions.move, actions.copy, actions.delete]);

  const handleUploadClick = useCallback(() => {
    uploadTargetPathRef.current = "";
    fileInputRef.current?.click();
  }, []);

  const handleUploadToPath = useCallback((parentPath: string) => {
    uploadTargetPathRef.current = parentPath;
    fileInputRef.current?.click();
  }, []);

  const handleFilesSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      const parentPath = uploadTargetPathRef.current;
      if (files.length > 0 && workspaceId) {
        uploadQueue.enqueueFiles(files, parentPath);
      }
      e.target.value = "";
      uploadTargetPathRef.current = "";
    },
    [uploadQueue, workspaceId],
  );

  const handleOsDropAtPath = useCallback(
    (parentPath: string, payload: WorkspaceDropPayload) => {
      if (!workspaceId) return;
      if (payload.tree) {
        uploadQueue.enqueueTree(payload.tree, parentPath);
        return;
      }
      if (payload.files.length > 0) {
        uploadQueue.enqueueFiles(payload.files, parentPath);
      }
    },
    [uploadQueue, workspaceId],
  );

  const getOsDropHandler = useCallback(
    (parentPath: string): FilesRailOsDropHandler =>
      (payload) =>
        handleOsDropAtPath(parentPath, payload),
    [handleOsDropAtPath],
  );

  return {
    fileRecords,
    isPending,
    isError,
    uploadQueue,
    fileInputRef,
    dialogs,
    actions,
    menuActions,
    handleUploadClick,
    handleUploadToPath,
    handleFilesSelected,
    handleOsDropAtPath,
    getOsDropHandler,
  };
}

export function useFilesRailWorkspaceRootItems({
  workspaceId,
  fileRecords,
  activeFileRecordId,
  onOpenFile,
  menuActions,
  getOsDropHandler,
  onUploadToPath,
  onCreateDocumentAtPath,
  onCreateSubfolder,
}: {
  workspaceId: string | undefined;
  fileRecords: FileRecord[] | undefined;
  activeFileRecordId: string | null;
  onOpenFile: (record: FileRecord) => void;
  menuActions?: FilesRailFileMenuActions;
  getOsDropHandler?: FilesRailOsDropAtPath;
  onUploadToPath?: (parentPath: string) => void;
  onCreateDocumentAtPath?: (parentPath: string) => void;
  onCreateSubfolder?: (parentPath: string) => void;
}) {
  return useFilesRailRootItems({
    records: fileRecords,
    workspaceId,
    activeFileRecordId,
    onOpenFile,
    menuActions,
    getOsDropHandler,
    onUploadToPath,
    onCreateDocumentAtPath,
    onCreateSubfolder,
  });
}
